import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getPriceTypeLabel, priceTypeEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateMachineTypeMutation, useUpdateMachineTypeMutation } from '../../../../hooks/mutations/useMachineTypeMutation';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { MachineType } from '../../../../services/apiSLTP/materials/machinesTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    machineType: MachineType | null;
    showEditModal: boolean;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateMachineTypeModal: React.FC<ModalProps> = ({ machineType, showEditModal, setShowEditModal, isEditMode = true, refetch }) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();

    const [selectedDesignation, setSelectedDesignation] = useState(machineType?.designation || '');
    const [selectedCrudStatus, setSelectedCrudStatus] = useState(machineType?.crudStatus || crudStatusEnum.OK);
    const [price, setPrice] = useState(typeof machineType?.price === 'number' ? machineType?.price.toString() : '');
    const [priceType, setPriceType] = useState({
        label: capitalizeFirstLetter(t(getPriceTypeLabel(machineType?.priceType || priceTypeEnum.DAY))),
        value: (machineType?.priceType || priceTypeEnum.DAY).toString(),
    });
    const [displayOrder, setDisplayOrder] = useState(typeof machineType?.displayOrder === 'number' ? machineType?.displayOrder.toString() : '');

    const { mutate: mutateMachineType } = useUpdateMachineTypeMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error updating machineType:', error.message),
    });

    const { mutate: addMachineType } = useCreateMachineTypeMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error adding machineType:', error.message),
    });

    const handleActiveChange = (isActive: boolean) => {
        setSelectedCrudStatus(isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE);
    };

    const isFormValid = () => {
        return !!(selectedDesignation.length && price.length);
    };

    const handleSubmit = () => {
        if (isEditMode) {
            mutateMachineType({
                machineTypeId: machineType?._id || '',
                data: {
                    designation: selectedDesignation,
                    crudStatus: selectedCrudStatus,
                    priceType: parseInt(priceType.value),
                    price: parseFloat(price),
                    displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
                },
            });
        } else {
            if (priceType)
                addMachineType({
                    designation: selectedDesignation,
                    crudStatus: selectedCrudStatus,
                    priceType: parseInt(priceType.value),
                    price: parseFloat(price),
                    displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
                });
        }
    };

    return (
        <ModalBody
            title={t('machine')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!isFormValid()}
            onSubmit={handleSubmit}
        >
            <CustomTextInput value={selectedDesignation} onChange={setSelectedDesignation} label={t('name')} required />
            <ToggleLine checked={selectedCrudStatus === crudStatusEnum.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput
                value={price}
                onChange={setPrice}
                label={t('price')}
                errorText={t('error')}
                required
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
            <DropDown
                items={Object.values(priceTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(t(getPriceTypeLabel(type))),
                    value: type.toString(),
                }))}
                selectedItem={priceType}
                handleChange={(e, value) => {
                    if (value) setPriceType(value);
                }}
                inputLabel={t('pricing')}
                required
            />
            <CustomTextInput value={displayOrder} onChange={setDisplayOrder} label={t('displayOrder')} type="number" />
        </ModalBody>
    );
};

export default UpdateMachineTypeModal;
